import type { HoverActionItem } from "@promptlayer/ui";
import { HoverActionButton, Message as SharedMessage, textToBlocks } from "@promptlayer/ui";
import { RotateCcw } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useCallback } from "react";

import { ChatMessage, VibeChatStore } from "./stores";
import { ComputerAgentDataStatus, MessageRole, ToolName } from "./types";
import { getVibeStreamingStatusLine } from "./utils/vibeStatusLine";
import { CreateAnalyticsGraphToolContent } from "./widgets/CreateAnalyticsGraphToolContent";
import { ComputerAgentWidget, ErrorMessageWidget, PulseDot, ToolCallWidget } from "./widgets";
import { getActiveToolProgressLabel } from "./toolConfig";
import { TextWithTags } from "./utils/TextWithTags";

import { CopyButton } from "@/components/ui/copy-button";
import { BasicTooltip } from "@/components/ui/Tooltip";
import type { AnalyticsGraphToolChartData } from "@/queries/analytics-custom-charts-types";

interface MessageProps {
  message: ChatMessage;
  isLoading: boolean;
  isLastMessage: boolean;
  chatStore: VibeChatStore;
  index?: number;
  onRetry?: (messageIndex?: number) => void;
}

const CHAT_MESSAGE_CHARACTER_WRAP_CLASS = "[overflow-wrap:anywhere]";
const WRANGLER_AGENT_ICON = (
  <img src="/wrangler-ai-icon.png" alt="Wrangler AI" className="h-5 w-5 object-contain" />
);

export const Message = observer(
  ({ message, isLoading, isLastMessage, chatStore, index = 0, onRetry }: MessageProps) => {
    const hasContent = Boolean(message.content);
    const hasTagMentions = message.content.includes("@@@tag:");
    const hasComputerAgent = Boolean(
      message.computerAgent && message.computerAgent.status === ComputerAgentDataStatus.RUNNING,
    );
    const isWaitingForResponse = isLoading && isLastMessage && !chatStore.hasStreamingStarted;
    const isToolCallRow = message.role === MessageRole.TOOL_CALL && message.toolCall;
    const showLoading =
      !isToolCallRow &&
      ((isLoading && isLastMessage && !hasContent && !hasComputerAgent) || isWaitingForResponse);
    const inFlightLabel = getActiveToolProgressLabel(chatStore.messages);
    const statusLine = getVibeStreamingStatusLine(inFlightLabel);
    const contentBlocks = textToBlocks(message.content);
    const recordAnalyticsGraphChartData = useCallback(
      (chartData: AnalyticsGraphToolChartData) => {
        chatStore.recordAnalyticsGraphChartData(message.toolCall?.id, chartData);
      },
      [chatStore, message.toolCall?.id],
    );

    if (hasContent && message.isError) {
      return (
        <ErrorMessageWidget
          errorMessage={message.errorMessage}
          isLoading={isLoading}
          onRetry={onRetry}
        />
      );
    }

    if (showLoading) {
      return (
        <div className="my-0.5 flex items-center gap-2">
          <PulseDot variant="blue" isAnimated className="m-0.5" />
          <span className="text-xs text-gray-500">{statusLine}</span>
        </div>
      );
    }

    switch (message.role) {
      case MessageRole.TOOL_CALL:
        if (message.toolCall) {
          switch (message.toolCall.name?.toLowerCase()) {
            case ToolName.COMPUTER_AGENT:
              return null;
            case ToolName.CREATE_ANALYTICS_GRAPH:
              return (
                <CreateAnalyticsGraphToolContent
                  toolCall={message.toolCall}
                  onChartDataChange={recordAnalyticsGraphChartData}
                />
              );
            default:
              return <ToolCallWidget toolCall={message.toolCall} />;
          }
        }
        break;

      case MessageRole.COMPUTER_AGENT:
        if (message.computerAgent) {
          return (
            <ComputerAgentWidget
              computerAgent={message.computerAgent}
              isStreaming={isLoading && isLastMessage}
              index={index}
            />
          );
        }
        break;

      case MessageRole.USER:
      case MessageRole.ASSISTANT:
        if (hasContent) {
          const isUser = message.role === MessageRole.USER;
          const useTagRenderer = isUser && hasTagMentions;
          const sharedMessageContent = useTagRenderer ? textToBlocks("") : contentBlocks;

          const extraHoverActions: HoverActionItem[] = [
            {
              key: "copy",
              node: (
                <CopyButton
                  text={message.content}
                  variant="ghost"
                  iconSize="h-3.5 w-3.5"
                  className="h-6 w-6 rounded-md p-0 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                />
              ),
            },
            ...(isUser && onRetry
              ? [
                  {
                    key: "retry",
                    node: (
                      <BasicTooltip content="Retry" side="left">
                        <HoverActionButton onClick={() => onRetry(index)} aria-label="Retry">
                          <RotateCcw className="h-3.5 w-3.5" />
                        </HoverActionButton>
                      </BasicTooltip>
                    ),
                  },
                ]
              : []),
          ];

          return (
            <SharedMessage
              id={message.taskId ?? `vibe-${index}`}
              role={isUser ? "user" : "assistant"}
              content={sharedMessageContent}
              renderUserContent={
                useTagRenderer
                  ? () => (
                      <span className={`whitespace-pre-wrap ${CHAT_MESSAGE_CHARACTER_WRAP_CLASS}`}>
                        <TextWithTags content={message.content} />
                      </span>
                    )
                  : undefined
              }
              isStreaming={!isUser && isLoading && isLastMessage && chatStore.isReceivingEvents}
              animateKey={message.taskId ?? `vibe-${index}`}
              timestamp={undefined}
              customAgentIcon={isUser ? undefined : WRANGLER_AGENT_ICON}
              extraHoverActions={extraHoverActions}
              hideCopyButton
              bubbleClassName={
                isUser ? "bg-blue-500 text-sm leading-relaxed text-white" : undefined
              }
              maxWidthClass={
                // Hover actions float outside the bubble (copy + retry for user,
                // copy for assistant) — reserve just enough room so they don't
                // overflow the container when the bubble is near full width.
                isUser ? "max-w-[calc(100%-3.5rem)]" : "max-w-[calc(100%-2rem)]"
              }
              className={CHAT_MESSAGE_CHARACTER_WRAP_CLASS}
            />
          );
        }
        break;

      default:
        break;
    }
  },
);
